/**
 * The table editors' models for the two known artifact schemas (authoring-ui.md AU-5): rows
 * as the form edits them, the canonical JSON content they are stored as, and the row
 * checks the form shows as hints. The server checks the artifact format itself; these
 * checks only say what a curator would ask about.
 *
 * - `dmw-term-glossary/1`: `{terms: [{key, label, de: [wording], en: [wording], note}]}`
 * - `dmw-disclosure-checklist/1`: `{items: [{id, requirement, reference, part_type, labels, xbrl_concept}]}`
 */

import { ANNOTATION_SET_SCHEMA, readAnnotationSetContent } from "../../../api/content/documentAnnotationsApi";
import { JsonContractError } from "../../../api/http/jsonDecoding";
import type { AnnotationSet } from "../../../api/threads/liveMessages";

export const GLOSSARY_SCHEMA = "dmw-term-glossary/1";
export const CHECKLIST_SCHEMA = "dmw-disclosure-checklist/1";
export const MARKDOWN_SCHEMA = "markdown";

/** The schemas the form has an editor for; any other is edited as JSON. */
export type ArtifactEditor = "markdown" | "glossary" | "checklist" | "annotations" | "json";

export function editorOf(mediaType: string, schema: string): ArtifactEditor {
  if (mediaType === "text/markdown") {
    return "markdown";
  }
  if (schema === GLOSSARY_SCHEMA) {
    return "glossary";
  }
  if (schema === ANNOTATION_SET_SCHEMA) {
    return "annotations";
  }
  return schema === CHECKLIST_SCHEMA ? "checklist" : "json";
}

/**
 * A content annotation set (W-15), edited in v1's span view; `null` if its content is not one,
 * so the form falls back to JSON as for the tables.
 */
export function annotationSetOf(content: unknown): AnnotationSet | null {
  try {
    return readAnnotationSetContent(content);
  } catch (caught) {
    if (caught instanceof JsonContractError) return null;
    throw caught;
  }
}

export interface GlossaryTerm {
  readonly key: string;
  /** A label key of the project's label list, or `""`. */
  readonly label: string;
  readonly de: readonly string[];
  readonly en: readonly string[];
  readonly note: string;
}

export interface ChecklistItem {
  readonly id: string;
  readonly requirement: string;
  readonly reference: string;
  readonly part_type: string;
  readonly labels: readonly string[];
  readonly xbrl_concept: string;
}

/** The glossary rows of stored content; `null` if the content is not a glossary. */
export function glossaryRows(content: unknown): readonly GlossaryTerm[] | null {
  const terms = memberArray(content, "terms");
  if (terms === null) {
    return null;
  }
  const rows: GlossaryTerm[] = [];
  for (const term of terms) {
    if (!isRecord(term)) {
      return null;
    }
    rows.push({ key: text(term["key"]), label: text(term["label"]), de: texts(term["de"]), en: texts(term["en"]), note: text(term["note"]) });
  }
  return rows;
}

export function glossaryContent(rows: readonly GlossaryTerm[]): { terms: GlossaryTerm[] } {
  return { terms: rows.map((row) => ({ key: row.key, label: row.label, de: [...row.de], en: [...row.en], note: row.note })) };
}

export function checklistRows(content: unknown): readonly ChecklistItem[] | null {
  const items = memberArray(content, "items");
  if (items === null) {
    return null;
  }
  const rows: ChecklistItem[] = [];
  for (const item of items) {
    if (!isRecord(item)) {
      return null;
    }
    rows.push({
      id: text(item["id"]),
      requirement: text(item["requirement"]),
      reference: text(item["reference"]),
      part_type: text(item["part_type"]),
      labels: texts(item["labels"]),
      xbrl_concept: text(item["xbrl_concept"]),
    });
  }
  return rows;
}

export function checklistContent(rows: readonly ChecklistItem[]): { items: ChecklistItem[] } {
  return { items: rows.map((row) => ({ ...row, labels: [...row.labels] })) };
}

/** A list of wordings as one cell: synonyms separated by `;`. */
export function wordingsText(wordings: readonly string[]): string {
  return wordings.join("; ");
}

export function wordingsOf(cell: string): readonly string[] {
  return cell
    .split(";")
    .map((wording) => wording.trim())
    .filter((wording) => wording !== "");
}

export type RowHint =
  | { readonly kind: "duplicateKey"; readonly key: string }
  | { readonly kind: "emptyKey"; readonly row: number }
  | { readonly kind: "unknownLabel"; readonly key: string; readonly label: string }
  | { readonly kind: "noLabel"; readonly key: string }
  | { readonly kind: "labelsWithoutTerm"; readonly labels: readonly string[] };

/**
 * What a curator would ask about a glossary: unique non-empty keys, labels that exist in
 * the label list, and labels no term covers. `labelKeys` is `null` when no label list is
 * known; label checks are then skipped.
 */
export function glossaryHints(rows: readonly GlossaryTerm[], labelKeys: readonly string[] | null): readonly RowHint[] {
  const hints: RowHint[] = [...keyHints(rows.map((row) => row.key))];
  if (labelKeys === null) {
    return hints;
  }
  for (const row of rows) {
    if (row.label === "") {
      hints.push({ kind: "noLabel", key: row.key });
    } else if (!labelKeys.includes(row.label)) {
      hints.push({ kind: "unknownLabel", key: row.key, label: row.label });
    }
  }
  const covered = new Set(rows.map((row) => row.label));
  const uncovered = labelKeys.filter((label) => !covered.has(label));
  if (uncovered.length > 0 && rows.length > 0) {
    hints.push({ kind: "labelsWithoutTerm", labels: uncovered });
  }
  return hints;
}

export function checklistHints(rows: readonly ChecklistItem[], labelKeys: readonly string[] | null): readonly RowHint[] {
  const hints: RowHint[] = [...keyHints(rows.map((row) => row.id))];
  if (labelKeys !== null) {
    for (const row of rows) {
      for (const label of row.labels) {
        if (!labelKeys.includes(label)) {
          hints.push({ kind: "unknownLabel", key: row.id, label });
        }
      }
    }
  }
  return hints;
}

function keyHints(keys: readonly string[]): RowHint[] {
  const hints: RowHint[] = [];
  const seen = new Set<string>();
  keys.forEach((key, index) => {
    if (key.trim() === "") {
      hints.push({ kind: "emptyKey", row: index + 1 });
    } else if (seen.has(key)) {
      hints.push({ kind: "duplicateKey", key });
    }
    seen.add(key);
  });
  return hints;
}

/**
 * Glossary rows from CSV with a header row naming `key`, `label`, `de`, `en` and `note`
 * (any order; missing columns stay empty). Fields may be quoted with `"`; wordings within a
 * cell are separated by `;`. Returns `null` when there is no `key` column.
 */
export function glossaryFromCsv(csv: string): readonly GlossaryTerm[] | null {
  const [header, ...records] = csvRecords(csv);
  if (header === undefined) {
    return null;
  }
  const column = (name: string) => header.findIndex((cell) => cell.trim().toLowerCase() === name);
  const key = column("key");
  if (key < 0) {
    return null;
  }
  const cell = (record: readonly string[], index: number) => (index < 0 ? "" : (record[index] ?? "").trim());
  return records
    .filter((record) => record.some((value) => value.trim() !== ""))
    .map((record) => ({
      key: cell(record, key),
      label: cell(record, column("label")),
      de: wordingsOf(cell(record, column("de"))),
      en: wordingsOf(cell(record, column("en"))),
      note: cell(record, column("note")),
    }));
}

/** RFC 4180 records: quoted fields may hold commas, line breaks and doubled quotes. */
export function csvRecords(csv: string): readonly (readonly string[])[] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < csv.length; index += 1) {
    const character = csv.charAt(index);
    if (quoted) {
      if (character === '"' && csv[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        field += character;
      }
    } else if (character === '"') {
      quoted = true;
    } else if (character === ",") {
      record.push(field);
      field = "";
    } else if (character === "\n" || character === "\r") {
      if (character === "\r" && csv[index + 1] === "\n") {
        index += 1;
      }
      record.push(field);
      records.push(record);
      record = [];
      field = "";
    } else {
      field += character;
    }
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records;
}

function memberArray(content: unknown, member: string): readonly unknown[] | null {
  if (!isRecord(content)) {
    return null;
  }
  const value = content[member];
  return Array.isArray(value) ? (value as readonly unknown[]) : null;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function texts(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((member): member is string => typeof member === "string") : [];
}
